import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    organization: "",
    role: "participant",
    program: "men",
  });
  const [err, setErr] = useState("");
  const [emailInUse, setEmailInUse] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    setEmailInUse(false);
    try {
      const u = await register(form);
      toast.success("Your path begins.");
      nav(u.role === "facilitator" ? "/facilitator" : "/dashboard");
    } catch (e) {
      const detail = e.detail;
      const msg = typeof detail === "string" ? detail : e.message;
      if (e.status === 403 && /already in use/i.test(msg || "")) {
        setEmailInUse(true);
      }
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16 grain relative" data-testid="register-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(201,169,97,0.08),_transparent_60%)]" />
      <div className="relative card-bento w-full max-w-xl">
        <div className="overline mb-3">Enroll</div>
        <h1
          className="text-3xl md:text-4xl text-[#F7F5F0] mb-2"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          Begin the path.
        </h1>
        <p className="text-sm text-[#8A8782] mb-8">
          Open to participants, facilitators, and partner institutions.
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="register-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2">Full Name</label>
              <input
                required
                value={form.name}
                onChange={set("name")}
                className="input-tcpi"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                className="input-tcpi"
                data-testid="register-email-input"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2">Password</label>
              <input
                type="password"
                minLength={6}
                required
                value={form.password}
                onChange={set("password")}
                className="input-tcpi"
                data-testid="register-password-input"
              />
            </div>
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2">Organization (optional)</label>
              <input
                value={form.organization}
                onChange={set("organization")}
                className="input-tcpi"
                placeholder="Nonprofit or institution"
                data-testid="register-org-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-[#D1CFC9] mb-2">I am a</label>
            <div className="grid grid-cols-2 gap-3">
              {["participant", "facilitator"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setForm({ ...form, role: r })}
                  className={`py-3 rounded-md border transition ${
                    form.role === r
                      ? "border-[#C9A961] bg-[#C9A961]/10 text-[#C9A961]"
                      : "border-[#C9A961]/20 text-[#D1CFC9] hover:border-[#C9A961]/50"
                  }`}
                  data-testid={`register-role-${r}`}
                >
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm text-[#D1CFC9] mb-2">Select your program</label>
            <p className="text-xs text-[#8A8782] mb-3">
              TCPI offers separate trauma-informed pathways. Choose the program that fits you.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setForm({ ...form, program: "men" })}
                className={`p-4 rounded-md border text-left transition ${
                  form.program === "men"
                    ? "border-[#C9A961] bg-[#C9A961]/10"
                    : "border-[#C9A961]/20 hover:border-[#C9A961]/50"
                }`}
                data-testid="register-program-men"
              >
                <div className="text-[#C9A961] text-base mb-1" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  Men & Young Men
                </div>
                <div className="text-xs text-[#8A8782]">Think. Grow. Lead.</div>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, program: "women" })}
                className={`p-4 rounded-md border text-left transition ${
                  form.program === "women"
                    ? "border-[#e8a3b8] bg-[#e8a3b8]/10"
                    : "border-[#C9A961]/20 hover:border-[#e8a3b8]/60"
                }`}
                data-testid="register-program-women"
                style={form.program === "women" ? { color: "#e8a3b8" } : {}}
              >
                <div className="text-base mb-1" style={{ fontFamily: "Cormorant Garamond, serif", color: form.program === "women" ? "#e8a3b8" : "#C9A961" }}>
                  Women & Young Girls
                </div>
                <div className="text-xs text-[#8A8782]">Heal. Learn. Lead.</div>
              </button>
            </div>
          </div>

          {err && (
            <div className="text-sm text-red-400 space-y-2" data-testid="register-error">
              <div>{err}</div>
              {emailInUse && (
                <div className="text-xs text-[#D1CFC9] not-italic" data-testid="register-cohort-hint">
                  Are you joining a cohort or partner program that shares this email?
                  Your facilitator can enroll you from their admin panel — give them
                  your full name and they'll add you in a minute.
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full disabled:opacity-60"
            data-testid="register-submit-btn"
          >
            {loading ? "Creating account…" : "Create Account"}
          </button>
        </form>

        <div className="mt-6 text-sm text-center text-[#8A8782]">
          Already enrolled?{" "}
          <Link to="/login" className="text-[#C9A961] hover:underline" data-testid="register-go-login">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
